// FILE: UserInputWorkEntryRow.tsx
// Purpose: Show, in the transcript, the questions an agent asked and what the user answered.
// Layer: Web chat rendering
// Exports: UserInputWorkEntryRow

import { memo } from "react";
import type { WorkLogUserInput, WorkLogUserInputQuestion } from "../../session-logic";
import { CheckIcon, ChevronDownIcon, HelpCircleIcon } from "~/lib/icons";
import { cn } from "~/lib/utils";

/**
 * The heading of the row, which says which of the three outcomes this is.
 *
 * A pending question is the only one still asking something of whoever is
 * reading, so it says so first; the other two are history.
 */
function userInputHeading(userInput: WorkLogUserInput): string {
  switch (userInput.status) {
    case "pending":
      return "Esperando tu respuesta";
    case "cancelled":
      return "Pregunta cancelada";
    case "answered":
    default:
      return userInput.questions.length > 1 ? "Decisiones tomadas" : "Decisión tomada";
  }
}

/** What the user answered one question, as a single readable line. */
export function answerText(question: WorkLogUserInputQuestion): string | null {
  const parts = [...question.selectedLabels];
  if (question.customAnswer !== undefined) {
    parts.push(question.customAnswer);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

export const UserInputWorkEntryRow = memo(function UserInputWorkEntryRow(props: {
  userInput: WorkLogUserInput;
  fontSizePx: number;
  metaFontSizePx: number;
  expanded: boolean;
  onToggleExpanded?: (() => void) | undefined;
  canReopen: boolean;
  onReopen?: (() => void) | undefined;
}) {
  const { userInput, fontSizePx, metaFontSizePx, expanded, onToggleExpanded, canReopen, onReopen } =
    props;
  const pending = userInput.status === "pending";
  // Only a question with options left to show is worth expanding: an orphan
  // answer (question not on this page) carries none, so its row stays flat.
  const canExpand =
    Boolean(onToggleExpanded) &&
    userInput.questions.some((question) => question.options.length > 0);

  return (
    <div
      className={cn(
        "rounded-lg border py-1.5 pr-2 pl-2",
        pending
          ? "border-blue-500/25 bg-blue-500/[0.04]"
          : "border-border/40 bg-background/40 hover:border-border/60",
      )}
      data-user-input-status={userInput.status}
    >
      <button
        type="button"
        disabled={!canExpand}
        {...(canExpand ? { "aria-expanded": expanded } : {})}
        onClick={canExpand ? onToggleExpanded : undefined}
        className={cn(
          "flex w-full items-center gap-2 text-left",
          canExpand ? "cursor-pointer hover:opacity-90" : "cursor-default",
        )}
      >
        <HelpCircleIcon
          aria-hidden="true"
          className={cn(
            "size-4 shrink-0",
            pending ? "text-blue-400/80" : "text-muted-foreground/40",
          )}
        />
        <span
          className={cn(
            "min-w-0 flex-1 truncate font-system-ui",
            pending ? "text-blue-300/90" : "text-muted-foreground/70",
          )}
          style={{ fontSize: `${fontSizePx}px` }}
        >
          {userInputHeading(userInput)}
        </span>
        {canExpand ? (
          <ChevronDownIcon
            aria-hidden="true"
            className={cn(
              "size-3.5 shrink-0 text-muted-foreground/40 transition-transform duration-150",
              expanded ? "" : "-rotate-90",
            )}
          />
        ) : null}
      </button>

      <div className="mt-1 space-y-2">
        {userInput.questions.map((question) => (
          <UserInputQuestionBlock
            key={`${userInput.requestId}:${question.id}`}
            question={question}
            fontSizePx={fontSizePx}
            metaFontSizePx={metaFontSizePx}
            expanded={expanded}
          />
        ))}
      </div>

      {canReopen && onReopen ? (
        <button
          type="button"
          onClick={onReopen}
          className="mt-1.5 rounded-md border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-blue-300/90 transition-colors hover:bg-blue-500/20"
          style={{ fontSize: `${metaFontSizePx}px` }}
        >
          Responder
        </button>
      ) : null}
    </div>
  );
});

const UserInputQuestionBlock = memo(function UserInputQuestionBlock({
  question,
  fontSizePx,
  metaFontSizePx,
  expanded,
}: {
  question: WorkLogUserInputQuestion;
  fontSizePx: number;
  metaFontSizePx: number;
  expanded: boolean;
}) {
  const answer = answerText(question);
  // Expanded, the ticked option already says what was chosen, so repeating it
  // underneath is noise. The line stays for everything the option list cannot
  // say: a hand-written answer, and a question left unanswered.
  const showAnswerLine =
    !expanded ||
    question.options.length === 0 ||
    question.customAnswer !== undefined ||
    answer === null;
  // The header is a 12-character label ("Auth method"); the question itself is
  // the sentence that makes the answer mean something, so folded rows show the
  // question and keep the header as the small caption above it.
  return (
    <div className="min-w-0">
      <div
        className="truncate font-system-ui tracking-widest text-muted-foreground/45 uppercase"
        style={{ fontSize: `${metaFontSizePx}px` }}
      >
        {question.header}
      </div>
      <div
        className={cn("font-system-ui text-foreground/75", expanded ? "" : "line-clamp-2")}
        style={{ fontSize: `${fontSizePx}px` }}
      >
        {question.question}
      </div>

      {expanded && question.options.length > 0 ? (
        <div className="mt-1 space-y-0.5">
          {question.options.map((option) => {
            const chosen = question.selectedLabels.includes(option.label);
            return (
              <div
                key={`${question.id}:${option.label}`}
                className={cn(
                  "flex items-start gap-1.5 rounded-md px-1.5 py-0.5",
                  chosen ? "bg-blue-500/10 text-foreground/90" : "text-muted-foreground/45",
                )}
              >
                <span className="flex size-3.5 shrink-0 items-center justify-center pt-0.5">
                  {chosen ? <CheckIcon className="size-3 text-blue-400" /> : null}
                </span>
                <span className="min-w-0 font-system-ui" style={{ fontSize: `${fontSizePx}px` }}>
                  <span className={chosen ? "font-medium" : undefined}>{option.label}</span>
                  {option.description && option.description !== option.label ? (
                    <span
                      className="ml-1.5 text-muted-foreground/40"
                      style={{ fontSize: `${metaFontSizePx}px` }}
                    >
                      {option.description}
                    </span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      {showAnswerLine ? (
        <div className="mt-0.5 flex items-baseline gap-1.5">
          <span
            className="shrink-0 font-system-ui text-muted-foreground/40"
            style={{ fontSize: `${metaFontSizePx}px` }}
          >
            Respuesta
          </span>
          {answer ? (
            <span
              className={cn(
                "min-w-0 font-system-ui font-medium",
                expanded ? "break-words" : "truncate",
              )}
              style={{ fontSize: `${fontSizePx}px`, color: "var(--info-foreground)" }}
              title={answer}
            >
              {answer}
              {question.customAnswer !== undefined && question.selectedLabels.length === 0 ? (
                <span
                  className="ml-1.5 font-normal text-muted-foreground/40"
                  style={{ fontSize: `${metaFontSizePx}px` }}
                >
                  escrita a mano
                </span>
              ) : null}
            </span>
          ) : (
            <span
              className="font-system-ui text-muted-foreground/40 italic"
              style={{ fontSize: `${fontSizePx}px` }}
            >
              sin responder
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
});
